import { useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import {
  useFieldProfil, useTambahFieldProfil, useUbahFieldProfil,
} from '../../../domains/tenancy/api'
import {
  Baris, Bendera, Chip, Gagal, Isian, Kolom2, Memuat, Panel, Pilihan, Tabel, Tombol,
} from '../../../shared/ui'

const TIPE = ['teks', 'angka', 'email', 'telepon']

/**
 * Pertanyaan yang bot ajukan ke prospek — diatur tenant, tanpa deploy (ADR-063).
 *
 * TIDAK ADA TOMBOL HAPUS, dan itu bukan kelalaian. Yang ada "Matikan": bot berhenti
 * bertanya, isian yang sudah terkumpul tetap tersimpan dan tetap terlihat. Tombol
 * "Hapus" di sini akan terbaca seperti menyembunyikan pertanyaan, sementara yang
 * dilakukannya membuang jawaban ratusan prospek.
 *
 * Kolom "terisi" ada supaya penolakan pergantian tipe punya arti: 12 prospek sudah
 * menjawab, jadi mengubahnya jadi angka akan meninggalkan nilai yang tidak bisa dibaca
 * tipenya sendiri.
 */
export default function FieldProfil() {
  const { t } = useBahasa()
  const q = useFieldProfil()
  const tambah = useTambahFieldProfil()
  const ubah = useUbahFieldProfil()
  const [buka, setBuka] = useState(false)
  const [f, setF] = useState({ kunci: '', label: '', tipe: 'teks', wajib: true })

  return (
    <>

      {/* Penolakan datang dari backend sebagai kalimat yang sudah menyebut apa yang
          harus dilakukan ("matikan field ini dan buat field baru kalau memang butuh
          tipe lain"). Ditampilkan apa adanya. */}
      {(ubah.isError || tambah.isError) && (
        <Bendera nada="bad" role="alert">{(ubah.error || tambah.error)?.message}</Bendera>
      )}

      <Panel
        judul={t('fp.daftarJudul')}
        catatan={t('fp.daftarCatatan')}
        aksi={
          <Tombol onClick={() => { setBuka(!buka); tambah.reset() }}>
            {buka ? t('umum.batal') : t('fp.tambah')}
          </Tombol>
        }
      >
        {buka && (
          <div className="p-3.5 border-b border-line grid grid-cols-2 gap-2.5 max-w-[72ch]">
            {[['kunci', 'jumlah_karyawan'], ['label', 'jumlah karyawan']].map(([k, contoh]) => (
              <label key={k} className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                  {t(`fp.kol${k[0].toUpperCase()}${k.slice(1)}`)}
                </span>
                <Isian
                  value={f[k]}
                  placeholder={contoh}
                  onChange={(e) => setF({ ...f, [k]: e.target.value })}
                />
              </label>
            ))}
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                {t('fp.kolTipe')}
              </span>
              <Pilihan value={f.tipe} onChange={(e) => setF({ ...f, tipe: e.target.value })}>
                {TIPE.map((x) => <option key={x} value={x}>{x}</option>)}
              </Pilihan>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                {t('fp.kolWajib')}
              </span>
              <Pilihan
                value={f.wajib ? '1' : '0'}
                onChange={(e) => setF({ ...f, wajib: e.target.value === '1' })}
              >
                <option value="1">{t('fp.wajib')}</option>
                <option value="0">{t('fp.opsional')}</option>
              </Pilihan>
            </label>
            <div className="col-span-2">
              <Tombol
                disabled={!f.kunci.trim() || !f.label.trim() || tambah.isPending}
                onClick={() => tambah.mutate(f, {
                  onSuccess: () => {
                    setBuka(false)
                    setF({ kunci: '', label: '', tipe: 'teks', wajib: true })
                  },
                })}
              >
                {tambah.isPending ? t('umum.menyimpan') : t('fp.simpanField')}
              </Tombol>
              <p className="m-0 mt-2 text-[11.5px] text-ink3 max-w-[64ch]">
                {t('fp.kunciCatatan')}
              </p>
            </div>
          </div>
        )}

        {q.isLoading && <Memuat tinggi={5} />}
        {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

        {q.data && (
          <Tabel
            kepala={[
              t('fp.kolLabel'), t('fp.kolTipe'), t('fp.kolWajib'),
              t('fp.kolTerisi'), t('fp.kolKeadaan'), '',
            ]}
          >
            {q.data.map((d) => (
              <Baris key={d.kunci}>
                <td className={d.aktif ? 'font-semibold' : 'font-semibold text-ink3'}>
                  {d.label}
                  {/* Kunci teknis sebagai SUB-LABEL, bukan kolom sendiri. Ia dibaca cuma
                      saat orang mencocokkan baris ini dengan data ekspornya, dan kolom
                      penuh untuk itu memakan lebar dari yang dibaca tiap kali. */}
                  <span className="block font-mono text-[10.5px] font-normal text-ink3">
                    {d.kunci}{d.bawaan ? ` · ${t('fp.bawaan')}` : ''}
                  </span>
                </td>
                <td className="text-ink2">{d.tipe}</td>
                <td>
                  <Tombol
                    kecil
                    jenis="sekunder"
                    disabled={ubah.isPending}
                    onClick={() => ubah.mutate({ kunci: d.kunci, wajib: !d.wajib })}
                  >
                    {d.wajib ? t('fp.wajib') : t('fp.opsional')}
                  </Tombol>
                </td>
                {/* Inilah yang menjelaskan kenapa tipenya tidak bisa diubah.
                    ANGKANYA DIBERI SATUAN. "3" telanjang tidak menjawab "tiga apa" —
                    tiga prospek, tiga jawaban, tiga kali ditanyakan? Yang dihitung
                    `jumlah_terisi` adalah berapa PROSPEK yang punya isian untuk field
                    ini, dan kolomnya harus mengatakan itu. */}
                <td className="tabular text-[11.5px]">
                  {d.terisi === 0
                    ? <span className="text-ink3">{t('fp.belumAda')}</span>
                    : t('fp.nProspek', { n: d.terisi })}
                </td>
                <td>
                  <Chip nada={d.aktif ? 'ok' : 'idle'}>
                    {d.aktif ? t('fp.ditanyakan') : t('fp.tidakLagi')}
                  </Chip>
                </td>
                <td className="text-right">
                  {/* Field bawaan dipakai kode di beberapa tempat — tombolnya tidak
                      ditampilkan sama sekali, bukan ditampilkan lalu ditolak 409. */}
                  {d.bawaan ? (
                    <span className="text-[11.5px] text-ink3">{t('fp.bawaan')}</span>
                  ) : (
                    <Tombol
                      kecil
                      jenis="sekunder"
                      disabled={ubah.isPending}
                      onClick={() => ubah.mutate({ kunci: d.kunci, aktif: !d.aktif })}
                    >
                      {d.aktif ? t('fp.matikan') : t('fp.nyalakan')}
                    </Tombol>
                  )}
                </td>
              </Baris>
            ))}
          </Tabel>
        )}
      </Panel>

      {/* Kalimat ini DULU jadi catatan panel. Ia dipindah ke sini, bukan dibuang:
          catatan panel di rancangan menyebut CARA KERJANYA ("ditanyakan satu per satu"),
          sementara kalimat ini menjawab pertanyaan yang muncul belakangan dan selalu
          sama — "kalau saya matikan, datanya hilang?". Rancangan menaruh dua spanduk
          bersisian di tab ini; ini yang pertama. */}
      <Kolom2>
        <Bendera nada="info" judul={t('ta.fpNonaktifJudul')}>
          {t('ta.fpNonaktifIsi')}
        </Bendera>
        {/* WAJIB TERLALU BANYAK. Bot menanyakan field wajib satu per satu di dalam
            percakapan; makin banyak yang wajib, makin ia terasa seperti formulir — dan
            calon pembeli berhenti di tengah. Ambangnya 4, angka di rancangan. */}
        {(q.data ?? []).filter((d) => d.wajib && d.aktif).length >= 4 && (
          <Bendera
            nada="warn"
            judul={t('fp.terlaluWajibJudul', {
              n: (q.data ?? []).filter((d) => d.wajib && d.aktif).length,
            })}
          >
            {t('fp.terlaluWajibIsi')}
          </Bendera>
        )}
      </Kolom2>
    </>
  )
}
